import React from 'react';
import { Pencil, Ban } from 'lucide-react';
import { api, type HuntGameRowWithFee, type HuntInvitation, type Mark, type MarkCalendarEvent } from '../../api';
import { askForPush } from '../../push';
import { allRows, inviteRows } from '../../lib/skjutmeny';
import { TYPE, useTheme } from '../../theme';
import { Button, Dialog, ErrorState, Icon, Label, Loader, Switch } from '../../components/ui';
import { ANSWER_LABEL, HuntInvitationView } from './HuntInvitation';

/** Kalender → Visa inbjudan. Den inbjudne kan svara; jaktledaren ser kommentarer och kan ändra eller ställa in. */
export function InvitationDialog({ event, mark, canEdit, onClose, onEdit, onChanged }: {
  event: MarkCalendarEvent; canEdit: boolean; onClose: () => void;
  /** Ger skjutmenyn i aktuellt läge (avskjutningsplanen nu) i stället för ögonblicksbilden. */
  mark?: Pick<Mark, 'id' | 'gameTypesJson' | 'county'> | null;
  onEdit: (ev: MarkCalendarEvent) => void; onChanged: (ev: MarkCalendarEvent) => void;
}) {
  const T = useTheme();
  const [inv, setInv] = React.useState<HuntInvitation | null | undefined>(undefined);
  const [err, setErr] = React.useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = React.useState(false);
  const [notify, setNotify] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { api.huntInvitationForEvent(event.id).then(setInv).catch(e => setErr((e as Error).message)); }, [event.id]);
  // Skjutmenyn i aktuellt läge (planen nu) från API:t, med fällavgiften för den som tittar.
  const [liveGameRows, setLiveGameRows] = React.useState<HuntGameRowWithFee[] | null>(null);
  React.useEffect(() => {
    if (!mark?.id || !inv?.gameRows?.length) { setLiveGameRows(null); return; }
    let cancelled = false;
    api.markSkjutmeny(mark.id, { at: new Date(event.startTime).toISOString(), wholeDay: true })
      .then(m => {
        if (cancelled) return;
        const fees = new Map(allRows(m).map(r => [r.gameType, r]));
        setLiveGameRows(inviteRows(allRows(m)).map(r => ({ ...r, fee: fees.get(r.gameType)?.fieldFee ?? null, feeFrom: fees.get(r.gameType)?.feeFrom ?? false })));
      })
      .catch(() => { if (!cancelled) setLiveGameRows(null); });
    return () => { cancelled = true; };
  }, [mark?.id, inv?.gameRows?.length, event.startTime]);

  const cancelled = !!event.cancelledAt || event.status === 'cancelled';
  const editable = canEdit && !cancelled && event.status !== 'completed';
  const comments = event.invites.filter(i => i.comment);

  return (
    <Dialog open onClose={onClose} title="Inbjudan" width={640}
      actions={editable ? (confirmCancel ? (
        <>
          <Switch checked={notify} onChange={setNotify} label="Mejla deltagarna" />
          <span style={{ flex: 1 }} />
          <Button variant="secondary" onClick={() => setConfirmCancel(false)}>Avbryt</Button>
          <Button variant="danger" loading={busy} onClick={async () => {
            setBusy(true);
            try { const ev = await api.cancelPlannedHunt(event.id, notify); onChanged(ev); onClose(); } catch (e) { setErr((e as Error).message); }
            setBusy(false);
          }}>Ställ in jakten</Button>
        </>
      ) : (
        <>
          <Button variant="ghost" icon={<Icon as={Ban} size={18} />} onClick={() => setConfirmCancel(true)} style={{ color: T.danger }}>Ställ in</Button>
          <span style={{ flex: 1 }} />
          <Button variant="secondary" icon={<Icon as={Pencil} size={18} />} onClick={() => onEdit(event)}>Ändra jakten</Button>
        </>
      )) : undefined}>
      <div>
        {err && <ErrorState message={err} />}
        {!err && inv === undefined && <Loader text="Hämtar inbjudan…" />}
        {inv && (
          <HuntInvitationView inv={inv} liveGameRows={liveGameRows}
            onRespond={inv.token ? async (s, c) => { setInv(await api.respondToHuntInvitation(inv.token!, s, c)); void askForPush(); } : undefined} />
        )}
        {canEdit && comments.length > 0 && (
          <section style={{ marginTop: 16 }}>
            <Label>Kommentarer</Label>
            {comments.map(i => (
              <div key={i.id} style={{ ...TYPE.small, marginTop: 6, color: T.textSub }}><strong style={{ color: T.text }}>{i.name}</strong> ({ANSWER_LABEL[i.status] ?? i.status}): ”{i.comment}”</div>
            ))}
          </section>
        )}
      </div>
    </Dialog>
  );
}
